import { FC, useEffect } from "react";
import {
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { gameSetActualTeams } from "../../../openapi/api-client";
import { ResolvedTeamReference } from "../../../Components/ResolvedTeamReference";
import { teamRefToTeamId } from "../../../utils/teamRefToTeamId";
import { useCompetition } from "../../../hooks/useCompetition";
import { useTeamList } from "../../../hooks/useTeamList";
import { components } from "../../../openapi/generated-types";

type Game = components["schemas"]["Game"];

interface SetActualTeamsModalProps {
  game: Game;
  onClose: () => void;
}

export const SetActualTeamsModal: FC<SetActualTeamsModalProps> = ({
  game,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const competition = useCompetition(game.competitionId);
  const teams = useTeamList();

  const form = useForm<{
    homeTeamId: string;
    guestTeamId: string;
  }>({
    initialValues: {
      homeTeamId: game.actualTeams.homeTeamId ?? "",
      guestTeamId: game.actualTeams.guestTeamId ?? "",
    },
  });

  const recommendedHomeTeamId = useQuery({
    queryKey: ["recommendedTeam", game.plannedTeams.home],
    queryFn: async () => {
      if (game.actualTeams.homeTeamId) {
        return game.actualTeams.homeTeamId;
      }
      return (
        (await teamRefToTeamId(game.plannedTeams.home, game.competitionId)) ??
        null
      );
    },
  });

  const recommendedGuestTeamId = useQuery({
    queryKey: ["recommendedTeam", game.plannedTeams.guest],
    queryFn: async () => {
      if (game.actualTeams.guestTeamId) {
        return game.actualTeams.guestTeamId;
      }
      return (
        (await teamRefToTeamId(game.plannedTeams.guest, game.competitionId)) ??
        null
      );
    },
  });

  useEffect(() => {
    if (recommendedHomeTeamId.isSuccess && !form.values.homeTeamId) {
      form.setFieldValue("homeTeamId", recommendedHomeTeamId.data ?? "");
    }
  }, [recommendedHomeTeamId.data, recommendedHomeTeamId.isSuccess]);

  useEffect(() => {
    if (recommendedGuestTeamId.isSuccess && !form.values.guestTeamId) {
      form.setFieldValue("guestTeamId", recommendedGuestTeamId.data ?? "");
    }
  }, [recommendedGuestTeamId.data, recommendedGuestTeamId.isSuccess]);

  const setActualTeamsMutation = useMutation({
    mutationFn: gameSetActualTeams,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["game", { gameId: game.id }],
      });
      onClose();
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    const homeTeamId = values.homeTeamId || undefined;
    const guestTeamId = values.guestTeamId || undefined;

    await setActualTeamsMutation.mutateAsync({
      id: game.id,
      homeTeamId,
      guestTeamId,
    });
  });

  const isLoading =
    !competition.isSuccess ||
    !teams.isSuccess ||
    !recommendedHomeTeamId.isSuccess ||
    !recommendedGuestTeamId.isSuccess;

  const filteredTeams =
    teams.isSuccess && competition.isSuccess
      ? teams.data.teams.filter((t) => competition.data.teamIds.includes(t.id))
      : [];

  const canSubmit = form.values.homeTeamId || form.values.guestTeamId;

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={`Teams festlegen: ${game.name}`}
      size="md"
      closeOnClickOutside={false}
    >
      {isLoading ? (
        <Group justify="center" p="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Lade Vorschläge...
          </Text>
        </Group>
      ) : (
        <form onSubmit={handleSubmit}>
          <Stack>
            <Select
              label="Heim"
              placeholder="Team auswählen"
              disabled={!!game.actualTeams.homeTeamId}
              clearable
              description={
                <ResolvedTeamReference
                  teamReference={game.plannedTeams.home}
                  competitionId={game.competitionId}
                />
              }
              searchable
              nothingFoundMessage="Kein passendes Team gefunden"
              data={filteredTeams.map((t) => ({
                label: t.name,
                value: t.id,
              }))}
              {...form.getInputProps("homeTeamId")}
            />

            <Select
              label="Gast"
              placeholder="Team auswählen"
              disabled={!!game.actualTeams.guestTeamId}
              clearable
              description={
                <ResolvedTeamReference
                  teamReference={game.plannedTeams.guest}
                  competitionId={game.competitionId}
                />
              }
              searchable
              nothingFoundMessage="Kein passendes Team gefunden"
              data={filteredTeams.map((t) => ({
                label: t.name,
                value: t.id,
              }))}
              {...form.getInputProps("guestTeamId")}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={onClose}>
                Abbrechen
              </Button>
              <Button
                type="submit"
                loading={setActualTeamsMutation.isPending}
                disabled={!canSubmit}
              >
                Teams festlegen
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
};
